import { useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

import { Avatar } from "@/components/Layout/Header";
import Text from "@/components/Text";
import { getInitialsFromString } from "@/utils/helpers";
import { Dispatch, RootState } from "@/utils/store";
import Button from "@/components/Button";
import useOnClickOutside from "@/hooks/useOnClickOutside";

const Holder = styled.div`
  display: flex;
`;

const Dialog = styled.div`
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  width: 256px;
  background: ${({ theme }) => theme.colors.white};
  position: absolute;
  right: 0;
  top: calc(100% + 14px);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  z-index: ${({ theme }) => theme.zIndex[5]};
`;

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: 12px;
  padding: 0;
  font-size: 14px;
  height: 40px;
`;

const AvatarHolder = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

const Link = styled(NavLink)`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
    font-weight: ${theme.typography.button.fontWeight};
  `}
  font-size: 11px;
  line-height: 15px;
  display: flex;
  align-items: center;
  width: 100%;
  color: ${({ theme }) => theme.colors.gray};
  border-radius: 4px;
  transition: all ease-out 0.2s;
  height: 32px;
  padding-left: 14px;
  svg {
    margin-right: 14px;
  }
  &:hover,
  &:active {
    background: ${({ theme }) => theme.colors.grayscaleLight};
    color: ${({ theme }) => theme.colors.black};
  }
`;

const GuideSettingsHolder = styled.div`
  width: 100%;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
`;

const AuthButton = styled.button`
  display: inline-flex;
  align-items: center;
  svg {
    margin-left: 8px;
  }
`;

const ArrowDown = styled.svg<{ $rotate: boolean }>`
  transition: 0.2s ease-out transform;
  ${({ $rotate }) =>
    !$rotate &&
    css`
      transform: rotate(180deg);
    `}
`;

const guideSettingsEmails = [
  "andrius.sadauskas@digitalaz.com",
  "marius.klivecka@digitalaz.com",
  "lukasv@boredpanda.com",
  "andrejui.zem@gmail.com",
];

const AuthModal = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const user = useSelector((state: RootState) => state.auth.user!);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setIsAuthModalOpen(false));

  const isLoggingOut = useSelector(
    (state: RootState) => state.loading.effects.auth.logout
  );

  const dispatch = useDispatch<Dispatch>();

  const onLogout = async () => {
    try {
      await dispatch.auth.logout();
    } catch (err) {
      alert("An error occurred!");
      window.location.reload();
    }
  };

  return (
    <Holder ref={ref}>
      <AuthButton onClick={() => setIsAuthModalOpen((prev) => !prev)}>
        {user.avatar ? (
          <Avatar as="img" src={user.avatar} alt={user.name} />
        ) : (
          <Avatar>
            <Text as="span" $type="smallBold">
              {getInitialsFromString(user.name)}
            </Text>
          </Avatar>
        )}
        <Text as="span" $type="smallBold">
          <>{user.name}</>
        </Text>
        <ArrowDown
          $rotate={isAuthModalOpen}
          width="8"
          height="4"
          viewBox="0 0 8 4"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.33334 3.33334L3.94419 0.722497L6.55504 3.33334"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </ArrowDown>
      </AuthButton>
      {!!isAuthModalOpen && (
        <Dialog>
          {user.avatar ? (
            <AvatarHolder>
              <Avatar src={user.avatar} alt={user.name} as="img" />
              <div>
                <Text $type="smallBold">{user.name}</Text>
                <Text $type="smallMedium" $color="gray">
                  {user.email}
                </Text>
              </div>
            </AvatarHolder>
          ) : (
            <AvatarHolder>
              <Avatar>
                <Text as="span" $type="smallBold">
                  {getInitialsFromString(user.name)}
                </Text>
              </Avatar>
              <div>
                <Text $type="smallBold">{user.name}</Text>
                <Text $type="smallMedium" $color="gray">
                  {user.email}
                </Text>
              </div>
            </AvatarHolder>
          )}
          {guideSettingsEmails.includes(user.email) && (
            <GuideSettingsHolder>
              <Link
                to={`/general/view-guide-settings`}
                onClick={() => setIsAuthModalOpen(false)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.5 1.5H5.5V4H10.5V1.5Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11 2.5H13.4C13.5591 2.5 13.7117 2.55497 13.8243 2.65281C13.9368 2.75066 14 2.88337 14 3.02174V13.9783C14 14.1166 13.9368 14.2493 13.8243 14.3472C13.7117 14.445 13.5591 14.5 13.4 14.5H2.6C2.44087 14.5 2.28826 14.445 2.17574 14.3472C2.06321 14.2493 2 14.1166 2 13.9783V3.02174C2 2.88337 2.06321 2.75066 2.17574 2.65281C2.28826 2.55497 2.44087 2.5 2.6 2.5H5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 9.5H10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 7.5H10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                View guide settings
              </Link>
            </GuideSettingsHolder>
          )}

          <StyledButton
            color="secondary"
            disabled={isLoggingOut}
            loading={isLoggingOut}
            onClick={onLogout}
          >
            Sign out
          </StyledButton>
        </Dialog>
      )}
    </Holder>
  );
};

export default AuthModal;
